<template>
  <div class="image-details" ref="containerEl" :class="{fullscreen: fullscreen}" v-if="image">
    <div class="backdrop" @click="$emit('close-image')"></div>
    <div class="image-details__container shadow">
      <BNavbar data-bs-theme="dark" :class="{'show-navbar': showNavbar}"
               :fixed="fullscreen ? 'top' : undefined"
               :sticky="!fullscreen ? 'top' : undefined">
        <BNavbarNav>
          <BNavItem v-if="fullscreen" @click.prevent="emit('toggle-sidebar')"><Icon icon="bi:list" /></BNavItem>
          <BNavItem @click.prevent="loadFullVersion(undefined, true)"><Icon icon="bi:download" /></BNavItem>
          <BNavItem @click.prevent="loadFullVersion()"><Icon icon="bi:eye" /></BNavItem>
          <BNavItem v-if="!fullscreen" @click.prevent="startFullscreen()"><Icon icon="bi:fullscreen" /></BNavItem>
          <BNavItem v-if="fullscreen" @click.prevent="exitFullscreen()"><Icon icon="bi:fullscreen-exit" /></BNavItem>
        </BNavbarNav>
        <BNavbarNav>
          <BNavItem @click.prevent="$emit('close-image');" title="close"><Icon icon="bi:x" /></BNavItem>
        </BNavbarNav>
      </BNavbar>
      <ImageContainer ref="imageContainerRef" :source="imageSource(image.id)"
             :class="{ 'zoom-active': zoomActive }"
             :style="zoomActive ? { '--pan-x': panX + 'px', '--pan-y': panY + 'px', height: zoomContainerHeight + 'px' } : {}"
             @click="zoomActive && toggleZoom()"
             @dblclick="!zoomActive && (fullscreen && exitFullscreen() || startFullscreen())"
             @mousemove="onImageMouseMove"
             @touchstart.passive="onTouchStart"
             @touchmove="onTouchMove">
        <div class="last-image" @click="previousImage()" v-if="previous"><Icon icon="bi:chevron-left" /></div>
        <div class="next-image" @click="nextImage()" v-if="next"><Icon icon="bi:chevron-right" /></div>
        <div class="image__title" :class="{hidden: hideTitle}">{{image.title}}</div>
        <div class="zoom-toggle" :key="image?.id" @click.stop="toggleZoom($event)"><Icon icon="bi:zoom-in" /></div>
        <div class="slideshow__controls fab-container" v-if="slideshowActive">
          <BButton variant="light" size="lg" class="btn-fab" v-if="slideshowRunning"
                    @click="pauseSlideshow()"><Icon icon="bi:pause-fill" /></BButton>
          <BButton variant="light" size="lg" class="btn-fab" v-if="!slideshowRunning"
                    @click="continueSlideshow()"><Icon icon="bi:play-fill" /></BButton>
          <BButton variant="light" size="sm" class="btn-fab"
                    @click="stopSlideshow()"><Icon icon="bi:stop-fill" /></BButton>
        </div>
      </ImageContainer>
      <div class="info">
        <div class="info__title">
          {{image.title}} <small v-if="image.description"><br />{{image.description}}</small>
        </div>
        <p>
          <image-property icon="date" label="Aufnahmedatum" :value="formatDate(image.shot)" />
        </p><p>
          <image-property icon="camera" label="Kamera" :value="image.activeVersion.model" />
          <image-property v-if="image.activeVersion.lensModel"
                          icon="lens" label="Objektiv" :value="image.activeVersion.lensModel" />
        </p><p>
          <image-property v-if="image.activeVersion.focalLength35mm"
                          icon="focal-length" label="Brennweite"
                          :value="image.activeVersion.focalLength35mm+'mm'" />
          <image-property v-if="image.activeVersion.aperture"
                          icon="aperture" label="Blende"
                          :value="'f/'+image.activeVersion.aperture" />
          <image-property v-if="image.activeVersion.exposureTime"
                          icon="exposure" label="Belichtungszeit"
                          :value="image.activeVersion.exposureTime+'s'" />
          <image-property v-if="image.activeVersion.sensitivity"
                          icon="iso" label="Empfindlichkeit"
                          :value="'ISO'+image.activeVersion.sensitivity" />
          <image-property v-if="image.activeVersion.width && image.activeVersion.height"
                          icon="dimensions" label="Bildgröße"
                          :value="image.activeVersion.width + ' x ' + image.activeVersion.height + ' px'" />
        </p>
      </div>
      <div class="versions" v-if="image.versions.length > 1">
        <div class="versions__container" v-for="version in image.versions" :key="version.id">
          <div class="versions__selector">
            <Icon v-if="version.id === image.activeVersion.id" icon="bi:caret-right-fill" />
            <Icon v-else icon="bi:caret-right" />
          </div>
          <div class="versions__image">
            <img :src="'/api/versions/'+version.id+'/thumbnail.png?height=64'" />
          </div>
          <div class="versions__info">
            {{version.path.substring(version.path.lastIndexOf('/') + 1)}}
            <div class="versions__details">
              <image-property icon="time" label="Bearbeitet" :value="formatDateTime(version.mtime)" />
            </div>
          </div>
          <div class="versions__actions">
            <BButton variant="link" @click="loadFullVersion(version.id)"><Icon icon="bi:eye" /></BButton><br />
            <BDropdown toggleClass="image__menu-toggle text-decoration-none"
                        variant="link" noCaret end>
              <template #button-content><Icon icon="bi:list" /></template>
              <BDropdownItem><Icon icon="bi:caret-right" /> &nbsp;Use This Version</BDropdownItem>
              <BDropdownItem @click="loadFullVersion(version.id, true)"><Icon icon="bi:download" /> &nbsp;Download</BDropdownItem>
            </BDropdown>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import ImageContainer from '@/components/ImageContainer.vue'
import ImageProperty from '@/components/ImageProperty.vue'
import { Icon } from '@iconify/vue'
import { BNavbar, BNavbarNav, BNavItem, BButton, BDropdown, BDropdownItem } from 'bootstrap-vue-next'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import Image from '@/models/Image'
import { windowWidth, slideshowSpeed } from '@/stores/ui'

const props = defineProps<{ images: Image[]; startImage: Image }>()
const emit = defineEmits<{
  'close-image': []
  'switched-image': [payload: { image: Image }]
  'entered-fullscreen': []
  'toggle-sidebar': []
}>()

const containerEl = ref<HTMLElement | null>(null)
const imageContainerRef = ref<InstanceType<typeof ImageContainer> | null>(null)

const image = ref<Image | undefined>(undefined)
const fullscreen = ref(false)
let pos = 0
const history = ref<Image[]>([])
const queue = ref<Image[]>([])

const showNavbar = ref(true)
let navBarTimer: number | null = null

const hideTitle = ref(true)
let titleTimer: number | null = null

const slideshowActive = ref(false)
const slideshowRunning = ref(false)
let slideshowTimer: number | null = null

const zoomActive = ref(false)
const zoomContainerHeight = ref(0)
const panX = ref(0)
const panY = ref(0)
let touchStartX = 0, touchStartY = 0, touchStartPanX = 0, touchStartPanY = 0

const next = computed(() => queue.value.length > 0)
const previous = computed(() => history.value.length > 0)

function formatDate(date: string|Date): string { return format(new Date(date), 'PPp', { locale: de }) }
function formatDateTime(date: string|Date): string { return format(new Date(date), 'PPpp', { locale: de }) }

function setImage(img: Image) {
  image.value = img
  image.value.versions?.sort((a, b) => new Date(a.mtime).getTime() - new Date(b.mtime).getTime())
}

// created logic
if (props.startImage) {
  setImage(props.startImage)
  const p = props.images.indexOf(props.startImage)
  queue.value = props.images.slice(p + 1)
  history.value = props.images.slice(0, p)
} else {
  pos = 0
  setImage(props.images[pos])
  queue.value = props.images.slice(1)
}

function mouseMoved(ev: MouseEvent) {
  if (ev.y < window.innerHeight / 3) displayNavBar(2000)
}
function touchMoved() { displayNavBar(5000) }
function keyPressed(event: KeyboardEvent) {
  switch (event.key) {
    case 'ArrowRight': next.value && nextImage(); break
    case 'ArrowLeft': previous.value && previousImage(); break
    case ' ':
      if (slideshowActive.value) slideshowRunning.value ? pauseSlideshow() : continueSlideshow()
      break
    case 'f': case 'F': fullscreen.value ? exitFullscreen() : startFullscreen(); break
    case 'Escape': emit('close-image'); break
    default: return
  }
  event.preventDefault()
}

function applyFullscreenState(isFullscreen: boolean) {
  fullscreen.value = isFullscreen
  zoomActive.value = false
  panX.value = 0
  panY.value = 0
  if (!isFullscreen) {
    if (slideshowActive.value) pauseSlideshow()
  } else {
    emit('entered-fullscreen')
  }
}

function onFullscreenChange() {
  applyFullscreenState(!!document.fullscreenElement)
}

function onWindowResize() {
  if (!window.matchMedia('(pointer: fine)').matches) return
  if (document.fullscreenElement) return
  const isFullscreen = window.outerHeight === window.innerHeight
  if (fullscreen.value !== isFullscreen) {
    applyFullscreenState(isFullscreen)
  }
}

onMounted(() => {
  document.body.className += ' modal-open'
  document.body.addEventListener('mousemove', mouseMoved)
  document.body.addEventListener('touchmove', touchMoved)
  document.body.addEventListener('keyup', keyPressed)
  document.addEventListener('fullscreenchange', onFullscreenChange)
  window.addEventListener('resize', onWindowResize)
  displayNavBar(3000)
  displayTitle(3000)
})

onUnmounted(() => {
  document.body.className = document.body.className.replace(/ ?modal-open/, '')
  document.body.removeEventListener('mousemove', mouseMoved)
  document.body.removeEventListener('touchmove', touchMoved)
  document.body.removeEventListener('keyup', keyPressed)
  document.removeEventListener('fullscreenchange', onFullscreenChange)
  window.removeEventListener('resize', onWindowResize)
})

function displayNavBar(timeout = 0) {
  showNavbar.value = true
  navBarTimer && clearTimeout(navBarTimer)
  if (timeout > 0) navBarTimer = setTimeout(() => { showNavbar.value = false }, timeout)
}

function displayTitle(timeout = 0) {
  hideTitle.value = false
  titleTimer && clearTimeout(titleTimer)
  if (timeout > 0) titleTimer = setTimeout(() => { hideTitle.value = true }, timeout)
}

function startFullscreen() {
  containerEl.value?.requestFullscreen()
    .then(() => displayTitle(3000))
    .catch(() => { fullscreen.value = false })
}

function exitFullscreen() {
  if (document.fullscreenElement) document.exitFullscreen()
  else applyFullscreenState(false)
}

const imageResolutions = {
  portrait:  [{x: 576, y: 1024}, {x: 1080, y: 1920}, {x: 1440, y: 2560}],
  landscape: [{x: 1366, y: 768}, {x: 1920, y: 1080}, {x: 2560, y: 1440}, {x: 3840, y: 2160}],
}

function imageSource(imageId: string) {
  let neededW: number, neededH: number;
  if (fullscreen.value) {
    neededW = window.innerWidth * 2;
    neededH = window.innerHeight * 2;
  } else {
    const container = containerEl.value?.querySelector('.image-container') as HTMLElement | null;
    neededW = (container?.clientWidth ?? windowWidth.value / 2) * 2;
    neededH = (container?.clientHeight ?? window.innerHeight - 104) * 2;
  }

  if (neededW > 3000 || neededH > 3000) {
    return '/api/versions/' + image.value?.activeVersion.id + '.jpg';
  }

  const list = neededH > neededW ? imageResolutions.portrait : imageResolutions.landscape;
  const res = list.find(r => r.x >= neededW && r.y >= neededH);
  if (!res) return '/api/versions/' + image.value?.activeVersion.id + '.jpg';
  return '/api/images/' + imageId + '/thumbnail.jpg?width=' + res.x + '&height=' + res.y;
}

function toggleZoom(ev?: MouseEvent) {
  if (zoomActive.value) {
    zoomActive.value = false
    zoomContainerHeight.value = 0
    panX.value = 0
    panY.value = 0
    return
  }
  const container = containerEl.value?.querySelector('.image-container') as HTMLElement | null
  if (!container) return
  zoomContainerHeight.value = container.offsetHeight
  zoomActive.value = true
  const img = container.querySelector('img') as HTMLImageElement
  const rect = container.getBoundingClientRect()
  const maxX = Math.max(0, img.naturalWidth - rect.width) / 2
  const maxY = Math.max(0, img.naturalHeight - rect.height) / 2
  panX.value = ev ? maxX - maxX * 2 * (ev.clientX - rect.left) / rect.width : 0
  panY.value = ev ? maxY - maxY * 2 * (ev.clientY - rect.top) / rect.height : 0
}

function onImageMouseMove(ev: MouseEvent) {
  if (!zoomActive.value) return
  const container = ev.currentTarget as HTMLElement
  const img = container.querySelector('img') as HTMLImageElement
  if (!img) return
  const rect = container.getBoundingClientRect()
  const maxX = Math.max(0, img.naturalWidth - rect.width) / 2
  const maxY = Math.max(0, img.naturalHeight - rect.height) / 2
  panX.value = maxX - maxX * 2 * (ev.clientX - rect.left) / rect.width
  panY.value = maxY - maxY * 2 * (ev.clientY - rect.top) / rect.height
}

function onTouchStart(ev: TouchEvent) {
  if (!zoomActive.value || ev.touches.length !== 1) return
  touchStartX = ev.touches[0].clientX
  touchStartY = ev.touches[0].clientY
  touchStartPanX = panX.value
  touchStartPanY = panY.value
}

function onTouchMove(ev: TouchEvent) {
  if (!zoomActive.value || ev.touches.length !== 1) return
  ev.preventDefault()
  const container = ev.currentTarget as HTMLElement
  const img = container.querySelector('img') as HTMLImageElement
  const rect = container.getBoundingClientRect()
  const maxX = Math.max(0, img.naturalWidth - rect.width) / 2
  const maxY = Math.max(0, img.naturalHeight - rect.height) / 2
  panX.value = Math.max(-maxX, Math.min(maxX, touchStartPanX + ev.touches[0].clientX - touchStartX))
  panY.value = Math.max(-maxY, Math.min(maxY, touchStartPanY + ev.touches[0].clientY - touchStartY))
}

function gotoImage(img: Image): Promise<void> {
  zoomActive.value = false
  setImage(img)
  return imageContainerRef.value!.loadImage(imageSource(img.id)).then(() => {
    displayTitle(4000)
    emit('switched-image', { image: image.value! })
  })
}

function nextImage(): Promise<void> {
  pos++
  const img = queue.value.shift()
  if (!img) { slideshowActive.value && stopSlideshow(); return Promise.reject('No more images') }
  image.value && history.value.push(image.value)
  return gotoImage(img)
}

function previousImage(): Promise<void> {
  pos--
  const img = history.value.pop()
  if (!img) return Promise.reject('No more images')
  image.value && queue.value.unshift(image.value)
  if (slideshowActive.value) pauseSlideshow()
  return gotoImage(img)
}

function loadFullVersion(versionId?: number, download = false) {
  versionId = versionId ?? image.value?.activeVersion.id
  let url = '/api/versions/' + versionId + '.jpg'
  if (download) url += '?download=true'
  window.open(url, 'gallery_version_' + versionId)
}

function startSlideshow(random = false) {
  slideshowActive.value = true
  if (!fullscreen.value) startFullscreen()
  if (random) {
    history.value = []
    queue.value = props.images.slice(0).sort(() => Math.random() - 0.5)
    const img = queue.value.shift()
    if (img) gotoImage(img)
  }
  slideshowRunning.value = true
  startTimer()
}

function stopSlideshow() {
  if (fullscreen.value) exitFullscreen()
  pauseSlideshow()
  slideshowActive.value = false
}

function pauseSlideshow() {
  slideshowRunning.value = false
  slideshowTimer && window.clearTimeout(slideshowTimer)
}

function continueSlideshow() {
  slideshowRunning.value = true
  nextImage().then(() => startTimer())
}

function startTimer() {
  slideshowTimer && window.clearTimeout(slideshowTimer)
  slideshowTimer = window.setTimeout(() => {
    nextImage().then(() => { if (slideshowRunning.value) startTimer() })
  }, slideshowSpeed.value)
}

defineExpose({ startSlideshow, exitFullscreen, slideshowActive })
</script>

<style lang="scss">
@import 'src/main.scss';

.image-details {
  position: fixed;
  top: 56px;
  width: 100%;
  max-height: calc(100dvh - 56px);
  overflow: auto;
  z-index: 10;

  .image-details__container {
    background: #222;
  }

  .backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(#000, 0.7);
    z-index: -1;
  }

  .navbar {
    background: darken($primary, 20);
    justify-content: space-between;
  }

  .image-container {
    max-height: calc(100vh - 56px - 48px);
    min-height: min(
        calc(100vh - 53px * 2),
        calc(min(100vw, #{map-get($grid-breakpoints, lg)}) / 3 * 2 - 1px)
    );
    background: #000;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    align-items: center;

    .image__title {
      font-size: 28px;
      position: absolute;
      right: 6rem;
      bottom: 2rem;
      -webkit-text-stroke: 0.5px #000;
      opacity: 0.8;
      z-index: 1;
      transition: all 1s;

      &.hidden {
        opacity: 0;
      }
    }

    img {
      max-width: 100%;
      max-height: calc(100vh - 56px - 48px);
    }

    .next-image, .last-image {
      position: absolute;
      height: 100%;
      width: 33%;
      opacity: 0.4;
      color: #fff;
      font-size: 32px;
      z-index: 2;
      cursor: pointer;

      svg {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
      }
    }

    .next-image {
      right: 0;
      svg {
        right: 0;
      }
    }

    .last-image {
      left: 0;
      svg {
        left: 0;
      }
    }

    .zoom-toggle {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 15%;
      aspect-ratio: 1;
      z-index: 3;
      cursor: zoom-in;
      display: flex;
      align-items: center;
      justify-content: center;

      @keyframes zoom-hint {
        from { opacity: 0.8; }
        to   { opacity: 0; }
      }

      svg {
        font-size: 2rem;
        color: #fff;
        filter: drop-shadow(0 0 4px rgba(0, 0, 0, 0.8));
        animation: zoom-hint 0.5s ease-in 0.3s both;
      }

      &:hover svg {
        animation: none;
        opacity: 0.6;
      }
    }

    &.zoom-active {
      overflow: hidden;
      cursor: crosshair;

      img {
        max-width: none !important;
        max-height: none !important;
        width: auto !important;
        height: auto !important;
        position: absolute !important;
        top: 50% !important;
        left: 50% !important;
        transform: translate(calc(-50% + var(--pan-x, 0px)), calc(-50% + var(--pan-y, 0px))) !important;
      }

      .next-image, .last-image, .zoom-toggle {
        display: none;
      }

      &::after {
        content: '';
        position: absolute;
        inset: 0;
        cursor: zoom-out;
        z-index: 1;
      }
    }
  }

  .info {
    padding: 8px;

    .info__title {
      margin-bottom: 8px;
      font-size: 18px;
    }

    p {
      margin-bottom: 4px;
    }
  }

  .versions {
    margin-top: 8px;

    .versions__container {
      margin-bottom: 4px;
      height: 64px;
      display: flex;
      align-items: center;
    }

    .versions__selector {
      font-size: 32px;
      position: relative;
      top: -2px;
    }

    .versions__image {
      height: 66px;
      overflow: hidden;
      background: black;
      text-align: center;
      border: #333 solid;
      border-width: 1px 0 1px 1px;
      border-radius: 10px 0 0 10px;

      img: {
        max-height: 64px;
        max-width: 128px;
      }
    }

    .versions__info {
      flex-grow: 1;
      border: #333 solid;
      border-width: 1px 0;
      height: 66px;
      padding: 4px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }

    .versions__actions {
      border: #333 solid;
      border-width: 1px 1px 1px 0;
      border-radius: 0 10px 10px 0;
      height: 66px;
      margin-right: 8px;

      .btn {
        font-size: 12px;
        height: 32px;
      }
    }
  }

  @include media-breakpoint-up(md) {
    bottom: 0;
    //padding-top: calc(15px + 38px);

    .image-details__container {
      width: calc(100dvw * 3/4);
      left: 50%;
      transform: translateX(-50%);
      position: absolute;
      padding-bottom: 16px;
    }

    .info {
      background: #222;
      padding: 16px;
    }

    .versions {
      margin-top: 16px;
      display: flex;
      flex-flow: row wrap;

      .versions__container {
        margin-bottom: 8px;
        width: 50%;
      }

      .versions__actions {
        margin-right: 16px;
      }
    }
  }

  @include media-breakpoint-up(lg) {
    .image-details__container {
      min-height: 0;
    }
  }

  @include media-breakpoint-up(xl) {
    .image-details__container {
      width: calc(100dvw * 1/2);
      min-width: 992px;
    }
  }

  /**************
   * FULLSCREEN *
   **************/
  &.fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    width: 100dvw;
    height: 100vh;
    max-height: none;
    overflow: auto;
    background: black;
    z-index: 1050;

    .image-details__container {
      max-width: none;
      width: 100%;
      min-height: 100%;
    }

    .navbar {
      transition: all 0.3s;
      transform: translateY(-100%);
      opacity: 0.9;

      &.show-navbar {
        transform: none;
        opacity: 0.9;
      }
    }

    .image-container {
      max-height: 100dvh;
      height: 100dvh;

      img {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        max-height: 100%;
        max-width: 100dvw;
      }
    }
  }
}
</style>
